import { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { Alert, Box, Button, CircularProgress, Link, Paper, Stack, Tooltip, Typography } from '../../ui/core';
import { WarningIcon } from '../../ui/icons';
import { surfaceCardSx as cardSx, cardHeaderSx, cardBodySx } from '../../ui/styles';
import ClearGuardDialog from './ClearGuardDialog';
import { EnumPill, GuardDescription, JsonBlock, Muted, SectionTitle, ShadowChip } from './parts';
import {
  GUARD_STATE_PALETTES,
  MODE_PALETTES,
  RESPONSE_PALETTES,
  SOURCE_PALETTES,
  formatNum,
  formatTs,
} from './sdpFormatters';

const POLICY_CHANGES_COMPACT = 8;

const Card = ({ title, action, children }) => (
  <Paper elevation={0} sx={cardSx}>
    <Box sx={{ ...cardHeaderSx, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
      <Typography sx={{ fontSize: '15px', fontWeight: 600 }}>{title}</Typography>
      {action}
    </Box>
    <Box sx={cardBodySx}>{children}</Box>
  </Paper>
);

const KV = ({ k, children }) => (
  <Box sx={{ display: 'flex', gap: '8px', fontSize: '13px', alignItems: 'baseline', flexWrap: 'wrap' }}>
    <Typography component="span" color="text.secondary" sx={{ fontSize: '13px', minWidth: 110 }}>
      {k}
    </Typography>
    <Box component="span" sx={{ fontSize: '13px' }}>
      {children}
    </Box>
  </Box>
);

const eventSummary = (e) => `${e.guard} ${e.metric} ${formatNum(e.value)} vs ${formatNum(e.threshold)}`;

const FlagsCard = ({ growthbook }) => {
  const { loaded, featureUrl, config, params } = growthbook || {};
  return (
    <Card
      title="Flags (read-only)"
      action={
        featureUrl ? (
          <Link href={featureUrl} target="_blank" rel="noopener noreferrer" sx={{ fontSize: '13px' }}>
            Edit in GrowthBook
          </Link>
        ) : null
      }
    >
      <Stack spacing="8px">
        {!loaded && (
          <Alert severity="error" variant="filled">
            GrowthBook not loaded: no city is owned. SDP sets no pay anywhere until sdp.config loads.
          </Alert>
        )}
        {loaded && config && config.problems && config.problems.length > 0 && (
          <Alert severity="warning">
            sdp.config is malformed: malformed fields are ignored (they never become ownership).
            <Box component="ul" sx={{ m: '4px 0 0', pl: '18px' }}>
              {config.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </Box>
          </Alert>
        )}
        <SectionTitle>sdp.config</SectionTitle>
        {config ? (
          <>
            <KV k="mode">
              <EnumPill value={config.mode} palettes={MODE_PALETTES} />
            </KV>
            <KV k="cities">{(config.cities || []).map((c) => c.cityName || c.cityId).join(', ') || '—'}</KV>
            <KV k="modeByCity">
              {(config.modeByCity || []).length === 0
                ? '—'
                : config.modeByCity.map((m) => `${m.cityName || m.cityId}: ${m.mode}`).join(', ')}
            </KV>
            <JsonBlock value={config.raw} />
          </>
        ) : (
          <Muted>No sdp.config value.</Muted>
        )}
        <SectionTitle>sdp.params</SectionTitle>
        {params && params.problems && params.problems.length > 0 && (
          <Alert severity="warning">
            sdp.params override rejected; reference params are used.
            <Box component="ul" sx={{ m: '4px 0 0', pl: '18px' }}>
              {params.problems.map((p) => (
                <li key={p}>{p}</li>
              ))}
            </Box>
          </Alert>
        )}
        {params && params.raw ? <JsonBlock value={params.raw} /> : <Muted>No override (reference params).</Muted>}
      </Stack>
    </Card>
  );
};

const ReferenceVersion = ({ label, reference }) => (
  <Box>
    <SectionTitle>{label}</SectionTitle>
    {reference ? (
      <Stack spacing="4px">
        <KV k="version">{`${reference.version} (${reference.status})`}</KV>
        <KV k="fittedAt">{formatTs(reference.fittedAt)}</KV>
        <KV k="window">{`${formatTs(reference.windowStart)} → ${formatTs(reference.windowEnd)}`}</KV>
        <KV k="thin">{reference.thin ? 'thin: yes' : 'thin: no'}</KV>
        <KV k="resetReason">{reference.resetReason || '—'}</KV>
      </Stack>
    ) : (
      <Muted>None synced yet.</Muted>
    )}
  </Box>
);

const ReferenceCard = ({ reference }) => (
  <Card title="Reference">
    <ReferenceVersion label="Active" reference={reference && reference.active} />
    <ReferenceVersion label="Last refit" reference={reference && reference.lastRefit} />
  </Card>
);

const OwnershipCell = ({ city }) => {
  const counts = city.pricingOwnerCounts || {};
  const landing = city.ownershipLanding || {};
  return (
    <Stack spacing="2px">
      <Typography sx={{ fontSize: '13px' }}>
        {`sdp ${counts.sdp ?? 0} · legacy ${counts.legacy ?? 0} · absent ${counts.absent ?? 0}`}
      </Typography>
      {landing.warn && (
        <Tooltip
          title={`Unclaimed trips should be priced by ${landing.expected} (absent counts as legacy). The ownership change ${
            landing.since ? `took effect ${formatTs(landing.since)}` : 'time is unknown'
          }, more than two ticks ago.`}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'warning.main', fontSize: '12px' }}>
            <WarningIcon fontSize="inherit" />
            <span>{`${landing.mismatched} not ${landing.expected}`}</span>
          </Box>
        </Tooltip>
      )}
    </Stack>
  );
};

const CitiesCard = ({ cities, onClear }) => (
  <Card title="Cities">
    <Alert severity="info" sx={{ mb: '12px', fontSize: '13px' }}>
      Only active cities are owned and paid by SDP. log_only cities have decisions, but their multipliers are SHADOW
      values that were never paid (legacy pricing pays). off cities have state but no decisions.
    </Alert>
    {cities.length === 0 ? (
      <Muted>No cities.</Muted>
    ) : (
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>City</TableCell>
              <TableCell>Mode</TableCell>
              <TableCell>Owned</TableCell>
              <TableCell>Guard state</TableCell>
              <TableCell>Live days</TableCell>
              <TableCell>Unclaimed by pricingOwner</TableCell>
              <TableCell align="right" />
            </TableRow>
          </TableHead>
          <TableBody>
            {cities.map((city) => (
              <TableRow key={city.cityId} data-testid={`city-row-${city.cityId}`}>
                <TableCell>
                  <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>{city.cityName || city.cityId}</Typography>
                  {!city.listed && (
                    <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                      not in sdp.config
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <EnumPill value={city.mode} palettes={MODE_PALETTES} />
                  {city.mode === 'log_only' && <ShadowChip mode={city.mode} />}
                </TableCell>
                <TableCell>{city.owned ? 'yes' : 'no'}</TableCell>
                <TableCell>
                  <Stack spacing="2px">
                    <Box>
                      <EnumPill value={city.guardState} palettes={GUARD_STATE_PALETTES} />
                      {city.guardReason && (
                        <Typography component="span" sx={{ fontSize: '12px', ml: '6px' }}>
                          {city.guardReason}
                        </Typography>
                      )}
                    </Box>
                    {(city.openEvents || []).map((e) => (
                      <Box key={e.id}>
                        <GuardDescription event={e} compact />
                        <Typography color="text.secondary" sx={{ fontSize: '12px' }}>
                          {`${eventSummary(e)} (${e.response}, since ${formatTs(e.ts)})`}
                        </Typography>
                      </Box>
                    ))}
                    {city.guardState !== 'normal' && !city.clearable && (
                      <Typography color="text.secondary" sx={{ fontSize: '12px', fontStyle: 'italic' }}>
                        Held by a global guard (see Global events); it can't be cleared per city.
                      </Typography>
                    )}
                  </Stack>
                </TableCell>
                <TableCell>{city.liveDays}</TableCell>
                <TableCell>
                  <OwnershipCell city={city} />
                </TableCell>
                <TableCell align="right">
                  {/* Only the city's OWN open non-alert events can be cleared; a
                      state held by a global event 409s per city. */}
                  {city.clearable && (
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      onClick={() =>
                        onClear({ scope: 'city', scopeId: city.cityId, name: city.cityName || city.cityId, guardState: city.guardState })
                      }
                    >
                      Clear
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )}
  </Card>
);

const EventLine = ({ event, name, action, testId }) => (
  <Box data-testid={testId} sx={{ py: '4px' }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
      <EnumPill value={event.response} palettes={RESPONSE_PALETTES} />
      <Typography sx={{ fontSize: '13px' }}>{`${name || event.scopeName || event.scope}: ${eventSummary(event)}`}</Typography>
      <Typography color="text.secondary" sx={{ fontSize: '12px' }}>{`since ${formatTs(event.ts)}`}</Typography>
      {action}
    </Box>
    <GuardDescription event={event} />
  </Box>
);

const OpenEventsCard = ({ riderSafeLanes, globalEvents, onClear }) => (
  <Card title="Open lane and global guards">
    <SectionTitle>Rider safe lanes</SectionTitle>
    {riderSafeLanes.length === 0 ? (
      <Muted>No lanes in rider safe mode.</Muted>
    ) : (
      riderSafeLanes.map(({ routeId, routeName, event }) => (
        <EventLine
          key={routeId}
          testId={`lane-${routeId}`}
          event={event}
          name={routeName || routeId}
          action={
            <Button
              size="small"
              variant="outlined"
              color="error"
              onClick={() => onClear({ scope: 'lane', scopeId: routeId, name: routeName || routeId, guardState: 'safe' })}
            >
              Clear lane
            </Button>
          }
        />
      ))
    )}
    <SectionTitle>Global events</SectionTitle>
    {globalEvents.length === 0 ? (
      <Muted>No open global events.</Muted>
    ) : (
      globalEvents.map((event) => <EventLine key={event.id} event={event} />)
    )}
  </Card>
);

const PolicyChangesCompact = ({ entries }) => (
  <Card title="Latest policy changes">
    {entries.length === 0 ? (
      <Muted>No policy changes.</Muted>
    ) : (
      <Stack spacing="4px">
        {entries.slice(0, POLICY_CHANGES_COMPACT).map((pc) => (
          <Box key={pc.id} sx={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            <Typography color="text.secondary" sx={{ fontSize: '12px', minWidth: 130 }}>
              {formatTs(pc.effectiveAt)}
            </Typography>
            <EnumPill value={pc.source} palettes={SOURCE_PALETTES} />
            {pc.hardReset && (
              <Typography component="span" sx={{ fontSize: '12px', fontWeight: 600, color: 'error.main' }}>
                hard reset
              </Typography>
            )}
            <Typography sx={{ fontSize: '13px' }}>{`${pc.affectsName || pc.affects}: ${pc.description}`}</Typography>
          </Box>
        ))}
        {entries.length > POLICY_CHANGES_COMPACT && (
          <Muted>{`${entries.length - POLICY_CHANGES_COMPACT} more on the Policy changes tab.`}</Muted>
        )}
      </Stack>
    )}
  </Card>
);

const StatusTab = ({ status, loading, error, onRefresh }) => {
  const [clearTarget, setClearTarget] = useState(null);

  if (!status) {
    if (error) return <Alert severity="error">{error}</Alert>;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', p: '24px' }}>
        <CircularProgress size={18} />
        <Muted>Loading status…</Muted>
      </Box>
    );
  }

  return (
    <Stack spacing="16px">
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Muted>{`Generated ${formatTs(status.generatedAt)}`}</Muted>
        <Button size="small" variant="outlined" onClick={onRefresh} disabled={loading}>
          Refresh
        </Button>
      </Box>
      {error && <Alert severity="error">{error}</Alert>}
      <FlagsCard growthbook={status.growthbook} />
      <CitiesCard cities={status.cities || []} onClear={setClearTarget} />
      <OpenEventsCard
        riderSafeLanes={status.riderSafeLanes || []}
        globalEvents={status.globalEvents || []}
        onClear={setClearTarget}
      />
      <ReferenceCard reference={status.reference} />
      <PolicyChangesCompact entries={status.policyChanges || []} />
      <ClearGuardDialog target={clearTarget} onClose={() => setClearTarget(null)} onCleared={() => onRefresh && onRefresh()} />
    </Stack>
  );
};

export default StatusTab;
